/**
 * DataGrid — the workhorse of a dense ERP.
 *
 * Deliberate choices, from DESIGN_SYSTEM §1 and §2.6:
 *  - 32px rows by default. Density is the point; generous space goes *around* tables, never inside them.
 *  - A real `<table>`, so screen readers and keyboard users get row and column semantics for free.
 *  - Sticky header on a sunken ground, hairline separators, no zebra striping (it fights the hairlines).
 *  - Numeric columns are right-aligned and tabular, because columns of figures exist to be compared.
 *  - An absent value renders `—`, never 0.
 *
 * On paging: the grid supports both a server mode (`page`/`pageCount`/`onPageChange`, the only honest
 * option once a register outgrows memory) and a client mode for the small fixed sets. It never *claims* a
 * total it has not been given — an estimated count is labelled as one.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, ChevronsUpDown, Columns3, X } from 'lucide-react';
import { Button, EmptyState, ErrorState, LoadingRows, Unknown, cx } from './primitives';

export interface Column<Row> {
  key: string;
  header: ReactNode;
  cell: (row: Row) => ReactNode;
  numeric?: boolean;
  mono?: boolean;
  width?: string;
  sortValue?: (row: Row) => string | number | null;
  align?: 'left' | 'right' | 'center';
  /** Hidden until the reader turns it on in the column chooser. */
  optional?: boolean;
  /** The language of the cells, for a screen reader; a key ending in _hi is Hindi without saying so. */
  lang?: string;
  /** Technical detail for administrators (a permission code, a table): kept out of the plain-language gate (v7 Part D §9). */
  technical?: boolean;
}

export interface BulkAction<Row> {
  key: string;
  label: string;
  /** Marked actions get the danger treatment and should confirm before running. */
  destructive?: boolean;
  onRun: (rows: readonly Row[]) => void | Promise<void>;
}

export interface DataGridProps<Row> {
  rows: readonly Row[] | undefined;
  columns: readonly Column<Row>[];
  rowKey: (row: Row) => string;
  isLoading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  onRowClick?: (row: Row) => void;
  emptyTitle?: string;
  emptyDetail?: ReactNode;
  density?: 'compact' | 'comfortable';
  footnote?: ReactNode;
  /** Renders above the header: filter controls, search, saved views. */
  toolbar?: ReactNode;
  bulkActions?: readonly BulkAction<Row>[];
  /** Server paging. Omit for client paging over whatever `rows` holds. */
  page?: number;
  pageCount?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  /** True when `total` came from a database estimate rather than an exact count. */
  totalIsEstimate?: boolean;
  total?: number;
  /** Persists the column chooser for this screen, per browser. */
  storageKey?: string;
}

export function DataGrid<Row>({
  rows, columns, rowKey, isLoading, error, onRetry, onRowClick,
  emptyTitle = 'Nothing to show', emptyDetail, density = 'compact', footnote,
  toolbar, bulkActions, page, pageCount, pageSize = 50, onPageChange, total, totalIsEstimate, storageKey,
}: DataGridProps<Row>) {
  const [sort, setSort] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [clientPage, setClientPage] = useState(0);
  const [chooserOpen, setChooserOpen] = useState(false);
  const headerCheckbox = useRef<HTMLInputElement>(null);
  // Keyboard (v7 Part D §10.2): J/K move along the rows, Enter opens one; with a row open (the peek), J/K open the next.
  const [active, setActive] = useState(-1);
  const [peeking, setPeeking] = useState(false);
  const tbodyRef = useRef<HTMLTableSectionElement>(null);

  const [hidden, setHidden] = useState<ReadonlySet<string>>(() => {
    const defaults = new Set(columns.filter((c) => c.optional).map((c) => c.key));
    if (!storageKey) return defaults;
    try {
      const raw = localStorage.getItem(`rtcom.columns.${storageKey}`);
      return raw ? new Set(JSON.parse(raw) as string[]) : defaults;
    } catch {
      return defaults;
    }
  });

  useEffect(() => {
    if (!storageKey) return;
    try {
      localStorage.setItem(`rtcom.columns.${storageKey}`, JSON.stringify([...hidden]));
    } catch {
      // Blocked storage only costs the reader their column choice next visit.
    }
  }, [hidden, storageKey]);

  const visibleColumns = useMemo(() => columns.filter((c) => !hidden.has(c.key)), [columns, hidden]);

  const sorted = useMemo(() => {
    if (!rows || !sort) return rows;
    const column = columns.find((c) => c.key === sort.key);
    if (!column?.sortValue) return rows;
    const factor = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = column.sortValue!(a);
      const bv = column.sortValue!(b);
      // Missing values always sort last, whichever way the column points: "unknown" is not "smallest".
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (av === bv) return 0;
      return (av < bv ? -1 : 1) * factor;
    });
  }, [rows, sort, columns]);

  const serverPaged = page !== undefined && onPageChange !== undefined;
  const visible = useMemo(() => {
    if (!sorted) return sorted;
    if (serverPaged) return sorted;
    return sorted.slice(clientPage * pageSize, (clientPage + 1) * pageSize);
  }, [sorted, serverPaged, clientPage, pageSize]);

  useEffect(() => {
    if (!onRowClick) return;
    const onKey = (event: KeyboardEvent) => {
      const t = event.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      const list = visible ?? [];
      if (!list.length) return;
      if (event.key === 'Escape') { setPeeking(false); return; }
      // Inside an open drawer only J/K act, and only while this grid opened it.
      const inDialog = !!t?.closest('[role="dialog"]');
      if (inDialog && !peeking) return;
      if (event.key === 'j' || event.key === 'k') {
        event.preventDefault();
        const next = Math.max(0, Math.min(list.length - 1, (active < 0 ? -1 : active) + (event.key === 'j' ? 1 : -1)));
        setActive(next);
        const tr = tbodyRef.current?.querySelectorAll('tr')[next] as HTMLElement | undefined;
        if (peeking) onRowClick(list[next]!); else tr?.focus();
      } else if (event.key === 'Enter' && active >= 0 && !inDialog && document.activeElement === document.body) {
        setPeeking(true); onRowClick(list[active]!);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onRowClick, visible, active, peeking]);

  // The header box shows "some of these" rather than lying with a tick or a blank.
  useEffect(() => {
    if (!headerCheckbox.current || !visible) return;
    const count = visible.filter((r) => selected.has(rowKey(r))).length;
    headerCheckbox.current.indeterminate = count > 0 && count < visible.length;
  }, [selected, visible, rowKey]);

  // An empty list keeps its toolbar: the button that fills it (Set a price, New …) lives there (the owner, 2026-10-03).
  // So does a list that is loading or failed, in the same place as the full grid's: the search box stays mounted and keeps
  // its focus while the next letter's rows load (the owner, 2026-10-07: "I have to click again to write the 2nd letter").
  const framed = (body: ReactNode) => (toolbar ? (
    <div className="data-grid flex min-h-0 flex-col">
      <div className="flex items-center gap-2 rounded-t-[calc(var(--radius-panel)-1px)] border-b border-line bg-surface-sunken px-3 py-1.5">
        <div className="min-w-0 flex-1">{toolbar}</div>
      </div>
      {body}
    </div>
  ) : body);
  if (error) return framed(<ErrorState error={error} retry={onRetry} />);
  if (isLoading) return framed(<LoadingRows columns={visibleColumns.length} />);
  if (!rows || rows.length === 0) return framed(<EmptyState title={emptyTitle} detail={emptyDetail} />);

  const rowHeight = density === 'compact' ? 'h-[var(--height-row)]' : 'h-[var(--height-row-comfortable)]';
  const selectable = Boolean(bulkActions?.length);
  const selectedRows = (rows ?? []).filter((r) => selected.has(rowKey(r)));

  const clientPageCount = Math.max(1, Math.ceil((sorted?.length ?? 0) / pageSize));
  const currentPage = serverPaged ? page! : clientPage;
  const totalPages = serverPaged ? (pageCount ?? 1) : clientPageCount;
  const goToPage = (next: number) => (serverPaged ? onPageChange!(next) : setClientPage(next));

  return (
    // data-grid: a grid placed straight on the page (not in a card) gets its own frosted backing over the pictures.
    <div className="data-grid flex min-h-0 flex-col">
      {toolbar || storageKey ? (
        // Its corners follow the panel's, so the sunken bar does not square off a rounded card (the owner, 2026-10-02).
        <div className="flex items-center gap-2 rounded-t-[calc(var(--radius-panel)-1px)] border-b border-line bg-surface-sunken px-3 py-1.5">
          <div className="min-w-0 flex-1">{toolbar}</div>
          {storageKey ? (
            <div className="relative">
              <Button size="dense" variant="ghost" onClick={() => setChooserOpen((o) => !o)} aria-expanded={chooserOpen}>
                <Columns3 size={13} aria-hidden />
                Columns
              </Button>
              {chooserOpen ? (
                <div
                  className="absolute right-0 z-20 mt-1 w-56 rounded-[var(--radius-panel)] border border-line bg-surface py-1 shadow-[var(--shadow-overlay)]"
                  role="group"
                  aria-label="Choose columns"
                >
                  {columns.map((column) => (
                    <label key={column.key} className="flex cursor-pointer items-center gap-2 px-3 py-1 text-body hover:bg-surface-sunken">
                      <input
                        type="checkbox"
                        checked={!hidden.has(column.key)}
                        onChange={() =>
                          setHidden((current) => {
                            const next = new Set(current);
                            if (next.has(column.key)) next.delete(column.key);
                            else next.add(column.key);
                            return next;
                          })
                        }
                        className="h-3.5 w-3.5 accent-[var(--color-brand)]"
                      />
                      <span className="truncate">{typeof column.header === 'string' ? column.header : column.key}</span>
                    </label>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* The bulk bar appears only when something is selected, and always says exactly how many. */}
      {selectable && selected.size > 0 ? (
        <div className="flex items-center gap-2 border-b border-line bg-brand-soft px-3 py-1.5">
          <span className="text-small font-medium text-brand">
            {selected.size} selected
          </span>
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            className="inline-flex items-center gap-1 text-small text-brand hover:underline"
          >
            <X size={11} aria-hidden />
            Clear
          </button>
          <div className="ml-auto flex items-center gap-2">
            {bulkActions!.map((action) => (
              <Button
                key={action.key}
                size="dense"
                variant={action.destructive ? 'danger' : 'secondary'}
                onClick={() => void action.onRun(selectedRows)}
              >
                {action.label}
              </Button>
            ))}
          </div>
        </div>
      ) : null}

      <div className={cx('min-h-0 overflow-auto', !(toolbar || storageKey) && 'rounded-t-[calc(var(--radius-panel)-1px)]')}>
        <table className="w-full border-collapse text-body">
          <thead className="grid-head sticky top-0 z-10">
            <tr className="bg-surface-sunken">
              {selectable ? (
                <th scope="col" className="w-8 border-b border-line px-3 py-1.5">
                  <input
                    ref={headerCheckbox}
                    type="checkbox"
                    aria-label="Select all rows on this page"
                    checked={Boolean(visible?.length) && visible!.every((r) => selected.has(rowKey(r)))}
                    onChange={(event) => {
                      const next = new Set(selected);
                      for (const row of visible ?? []) {
                        if (event.target.checked) next.add(rowKey(row));
                        else next.delete(rowKey(row));
                      }
                      setSelected(next);
                    }}
                    className="h-3.5 w-3.5 accent-[var(--color-brand)]"
                  />
                </th>
              ) : null}

              {visibleColumns.map((column) => {
                const sortable = Boolean(column.sortValue);
                const active = sort?.key === column.key;
                return (
                  <th
                    key={column.key}
                    scope="col"
                    style={column.width ? { width: column.width } : undefined}
                    className={cx(
                      'border-b border-line px-3 py-1.5 label-text whitespace-nowrap',
                      column.numeric || column.align === 'right' ? 'text-right' : 'text-left',
                      column.align === 'center' && 'text-center',
                    )}
                    aria-sort={active ? (sort!.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 [text-transform:inherit] [letter-spacing:inherit] hover:text-ink"
                        onClick={() =>
                          setSort((current) =>
                            current?.key === column.key
                              ? { key: column.key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
                              : { key: column.key, direction: 'asc' },
                          )
                        }
                      >
                        {column.header}
                        {active ? (
                          sort!.direction === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />
                        ) : (
                          <ChevronsUpDown size={11} className="opacity-40" />
                        )}
                      </button>
                    ) : (
                      column.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody ref={tbodyRef}>
            {(visible ?? []).map((row, rowIndex) => {
              const key = rowKey(row);
              const isSelected = selected.has(key);
              return (
                <tr
                  key={key}
                  onClick={onRowClick ? () => { setActive(rowIndex); setPeeking(true); onRowClick(row); } : undefined}
                  onFocus={onRowClick ? () => setActive(rowIndex) : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={
                    onRowClick
                      ? (event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setActive(rowIndex); setPeeking(true);
                            onRowClick(row);
                          }
                        }
                      : undefined
                  }
                  className={cx(
                    rowHeight,
                    'border-b border-line',
                    isSelected && 'bg-brand-soft',
                    rowIndex === active && onRowClick && 'bg-brand-soft/60',
                    onRowClick && 'cursor-pointer hover:bg-brand-soft focus:bg-brand-soft focus:outline-none',
                  )}
                >
                  {selectable ? (
                    <td className="px-3 align-middle" onClick={(event) => event.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select row ${key}`}
                        checked={isSelected}
                        onChange={() =>
                          setSelected((current) => {
                            const next = new Set(current);
                            if (next.has(key)) next.delete(key);
                            else next.add(key);
                            return next;
                          })
                        }
                        className="h-3.5 w-3.5 accent-[var(--color-brand)]"
                      />
                    </td>
                  ) : null}

                  {visibleColumns.map((column) => {
                    const content = column.cell(row);
                    const isEmpty = content === null || content === undefined || content === '';
                    return (
                      <td
                        key={column.key}
                        data-technical={column.technical ? '' : undefined}
                        lang={column.lang ?? (column.key.endsWith('_hi') ? 'hi' : undefined)}
                        className={cx(
                          'px-3 align-middle',
                          column.numeric && 'text-right tabular',
                          column.align === 'right' && 'text-right',
                          column.align === 'center' && 'text-center',
                          column.mono && 'code-text',
                        )}
                      >
                        {isEmpty ? <Unknown /> : content}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between gap-4 border-t border-line px-4 py-1.5 text-small text-ink-muted">
        <span>
          {total !== undefined ? (
            <>
              {totalIsEstimate ? 'about ' : ''}
              {total.toLocaleString()} {total === 1 ? 'row' : 'rows'}
              {totalIsEstimate ? <span className="ml-1 text-ink-faint">(estimated)</span> : null}
            </>
          ) : (
            <>
              {rows.length} {rows.length === 1 ? 'row' : 'rows'}
            </>
          )}
        </span>

        {totalPages > 1 ? (
          <span className="flex items-center gap-1">
            <Button
              size="dense"
              variant="ghost"
              disabled={currentPage <= 0}
              onClick={() => goToPage(currentPage - 1)}
              aria-label="Previous page"
            >
              <ChevronLeft size={13} />
            </Button>
            <span className="tabular px-1">
              {currentPage + 1} / {totalPages}
            </span>
            <Button
              size="dense"
              variant="ghost"
              disabled={currentPage >= totalPages - 1}
              onClick={() => goToPage(currentPage + 1)}
              aria-label="Next page"
            >
              <ChevronRight size={13} />
            </Button>
          </span>
        ) : null}

        {footnote ? <span className="truncate">{footnote}</span> : null}
      </div>
    </div>
  );
}
